import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calculator, TrendingUp, TrendingDown, DollarSign, Users, 
  Download, Calendar, FileText, PieChart, ArrowUpRight, ArrowDownRight,
  Wallet, CreditCard, RefreshCw
} from 'lucide-react';

const API = window.location.origin + '/api';

export default function Accounting() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [commissionReport, setCommissionReport] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [payoutReport, setPayoutReport] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Date filters
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [taxQuarter, setTaxQuarter] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Check permissions
  const canView = user?.role === 'admin' || user?.permissions?.accounting?.can_view;
  const canExport = user?.role === 'admin' || user?.permissions?.accounting?.can_view;

  const fetchDashboard = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate + 'T00:00:00');
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      
      const res = await axios.get(`${API}/admin/accounting/dashboard?${params}`, { headers });
      setDashboard(res.data);
    } catch (error) {
      toast.error('Failed to load accounting dashboard');
    }
  };

  const fetchCommissionReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate + 'T00:00:00');
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      
      const res = await axios.get(`${API}/admin/accounting/commission-report?${params}`, { headers });
      setCommissionReport(res.data);
    } catch (error) {
      toast.error('Failed to load commission report');
    }
  };

  const fetchTaxSummary = async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', taxYear);
      if (taxQuarter) params.append('quarter', taxQuarter);
      
      const res = await axios.get(`${API}/admin/accounting/tax-summary?${params}`, { headers });
      setTaxSummary(res.data);
    } catch (error) {
      toast.error('Failed to load tax summary');
    }
  };

  const fetchPayoutReport = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate + 'T00:00:00');
      if (endDate) params.append('end_date', endDate + 'T23:59:59');
      
      const res = await axios.get(`${API}/admin/accounting/payout-report?${params}`, { headers });
      setPayoutReport(res.data);
    } catch (error) {
      toast.error('Failed to load payout report');
    }
  };

  useEffect(() => {
    if (!canView) {
      navigate('/admin');
      return;
    }
    
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchCommissionReport(),
        fetchTaxSummary(),
        fetchPayoutReport()
      ]);
      setLoading(false);
    };
    fetchAll();
  }, [canView]);

  const handleDateFilter = () => {
    fetchDashboard();
    fetchCommissionReport();
    fetchPayoutReport();
  };

  const handleTaxFilter = () => {
    fetchTaxSummary();
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      
      if (type === 'tax-summary' || type === 'vat-report') {
        params.append('year', taxYear);
        if (taxQuarter) params.append('quarter', taxQuarter);
      } else {
        if (startDate) params.append('start_date', startDate + 'T00:00:00');
        if (endDate) params.append('end_date', endDate + 'T23:59:59');
      }
      
      const res = await axios.get(`${API}/admin/accounting/export/${type}?${params}`, {
        headers,
        responseType: format === 'csv' || format === 'pdf' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const blob = new Blob([res.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_${taxYear}${taxQuarter ? `_Q${taxQuarter}` : ''}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV exported successfully');
      } else if (format === 'pdf') {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_${taxYear}${taxQuarter ? `_Q${taxQuarter}` : ''}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF exported successfully');
      } else {
        // JSON - download as file
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_${startDate}_${endDate}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded successfully');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value || 0);
  };

  if (!canView) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar />
      <AdminMobileNav />
      
      <main className="md:ml-64 p-4 md:p-8 pt-16 md:pt-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]" data-testid="accounting-title">
              <Calculator className="inline w-6 h-6 mr-2" />
              Accounting
            </h1>
            <p className="text-slate-500 text-sm mt-1">Financial reports and analytics</p>
          </div>
          
          {/* Date Filter */}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
            <span className="text-slate-400">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
            <Button onClick={handleDateFilter} size="sm">
              <RefreshCw className="w-4 h-4 mr-1" />
              Apply
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 flex-wrap h-auto">
              <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
              <TabsTrigger value="commission">Commission</TabsTrigger>
              <TabsTrigger value="payouts">Payouts</TabsTrigger>
              <TabsTrigger value="tax">Tax Summary</TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              {dashboard && (
                <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Gross Revenue</p>
                            <p className="text-2xl font-bold text-slate-900">{formatCurrency(dashboard.summary.gross_revenue)}</p>
                          </div>
                          <div className="p-3 bg-green-100 rounded-xl">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Commission Earned</p>
                            <p className="text-2xl font-bold text-blue-600">{formatCurrency(dashboard.summary.total_commission)}</p>
                          </div>
                          <div className="p-3 bg-blue-100 rounded-xl">
                            <DollarSign className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Provider Payouts</p>
                            <p className="text-2xl font-bold text-amber-600">{formatCurrency(dashboard.summary.provider_payouts)}</p>
                          </div>
                          <div className="p-3 bg-amber-100 rounded-xl">
                            <Wallet className="w-6 h-6 text-amber-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-slate-500">Net Revenue</p>
                            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(dashboard.summary.net_revenue)}</p>
                          </div>
                          <div className="p-3 bg-emerald-100 rounded-xl">
                            <ArrowUpRight className="w-6 h-6 text-emerald-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Cash Flow */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Cash Flow</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                            <span className="text-sm font-medium">Total Top-ups</span>
                            <span className="font-bold text-green-600">{formatCurrency(dashboard.cash_flow.total_top_ups)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                            <span className="text-sm font-medium">Total Withdrawals</span>
                            <span className="font-bold text-red-600">{formatCurrency(dashboard.cash_flow.total_withdrawals)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                            <span className="text-sm font-medium">Pending Withdrawals</span>
                            <span className="font-bold text-amber-600">{formatCurrency(dashboard.cash_flow.pending_withdrawals)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Revenue by Category</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {dashboard.category_breakdown?.slice(0, 5).map((cat, i) => (
                            <div key={cat.category_name || `cat-${i}`} className="flex justify-between items-center">
                              <span className="text-sm">{cat.category_name}</span>
                              <div className="text-right">
                                <span className="font-bold">{formatCurrency(cat.gross_revenue)}</span>
                                <span className="text-xs text-slate-500 ml-2">({cat.booking_count} bookings)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Export Buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => handleExport('transactions', 'csv')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export Transactions (CSV)
                    </Button>
                    <Button variant="outline" onClick={() => handleExport('transactions', 'json')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Export Transactions (JSON)
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Commission Tab */}
            <TabsContent value="commission">
              {commissionReport && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Total Providers</p>
                        <p className="text-2xl font-bold">{commissionReport.totals.total_providers}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Total Bookings</p>
                        <p className="text-2xl font-bold">{commissionReport.totals.total_bookings}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Gross Revenue</p>
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(commissionReport.totals.gross_revenue)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-slate-500">Total Commission</p>
                        <p className="text-2xl font-bold text-blue-600">{formatCurrency(commissionReport.totals.total_commission)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Provider Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Provider Commission Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3">Provider</th>
                              <th className="text-right p-3">Bookings</th>
                              <th className="text-right p-3">Gross Revenue</th>
                              <th className="text-right p-3">Commission</th>
                              <th className="text-right p-3">Provider Earnings</th>
                              <th className="text-right p-3">Avg. Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {commissionReport.providers?.map((p, i) => (
                              <tr key={p.provider_id || `provider-${p.provider_email || i}`} className="border-b hover:bg-slate-50">
                                <td className="p-3">
                                  <div>{p.provider_name}</div>
                                  <div className="text-xs text-slate-500">{p.provider_email}</div>
                                </td>
                                <td className="text-right p-3">{p.total_bookings}</td>
                                <td className="text-right p-3 text-green-600">{formatCurrency(p.gross_revenue)}</td>
                                <td className="text-right p-3 text-blue-600">{formatCurrency(p.commission_earned)}</td>
                                <td className="text-right p-3">{formatCurrency(p.provider_earnings)}</td>
                                <td className="text-right p-3 text-slate-500">{formatCurrency(p.avg_booking_value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Export */}
                  <div className="flex gap-2 mt-4 flex-wrap">
                    <Button variant="outline" onClick={() => handleExport('commission-report', 'csv')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export Commission Report (CSV)
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Payouts Tab */}
            <TabsContent value="payouts">
              {payoutReport && (
                <>
                  {/* Status Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="border-amber-200 bg-amber-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-amber-700">Pending</p>
                        <p className="text-2xl font-bold text-amber-600">{payoutReport.summary.pending?.count || 0}</p>
                        <p className="text-sm text-amber-600">{formatCurrency(payoutReport.summary.pending?.amount)}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-blue-200 bg-blue-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-blue-700">Approved</p>
                        <p className="text-2xl font-bold text-blue-600">{payoutReport.summary.approved?.count || 0}</p>
                        <p className="text-sm text-blue-600">{formatCurrency(payoutReport.summary.approved?.amount)}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-green-700">Completed</p>
                        <p className="text-2xl font-bold text-green-600">{payoutReport.summary.completed?.count || 0}</p>
                        <p className="text-sm text-green-600">{formatCurrency(payoutReport.summary.completed?.amount)}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="p-4 text-center">
                        <p className="text-sm text-red-700">Rejected</p>
                        <p className="text-2xl font-bold text-red-600">{payoutReport.summary.rejected?.count || 0}</p>
                        <p className="text-sm text-red-600">{formatCurrency(payoutReport.summary.rejected?.amount)}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Withdrawals List */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Withdrawals</CardTitle>
                      <CardDescription>Total: {payoutReport.total_count} withdrawals ({formatCurrency(payoutReport.total_amount)})</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3">Date</th>
                              <th className="text-left p-3">Provider</th>
                              <th className="text-right p-3">Amount</th>
                              <th className="text-center p-3">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payoutReport.withdrawals?.slice(0, 20).map((w, i) => (
                              <tr key={w.id || `withdrawal-${w.created_at}-${i}`} className="border-b hover:bg-slate-50">
                                <td className="p-3 text-slate-500">{new Date(w.created_at).toLocaleDateString()}</td>
                                <td className="p-3">
                                  <div>{w.provider_name}</div>
                                  <div className="text-xs text-slate-500">{w.provider_email}</div>
                                </td>
                                <td className="text-right p-3 font-medium">{formatCurrency(w.amount)}</td>
                                <td className="text-center p-3">
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    w.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    w.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    w.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {w.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Export */}
                  <div className="flex gap-2 mt-4 flex-wrap">
                    <Button variant="outline" onClick={() => handleExport('payouts', 'csv')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export Payouts (CSV)
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Tax Summary Tab */}
            <TabsContent value="tax">
              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div>
                  <Label>Year</Label>
                  <Select value={String(taxYear)} onValueChange={(v) => setTaxYear(Number(v))}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quarter (optional)</Label>
                  <Select value={taxQuarter || "all"} onValueChange={(v) => setTaxQuarter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Full Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Full Year</SelectItem>
                      <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                      <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                      <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleTaxFilter} className="mt-6">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Load
                </Button>
              </div>

              {taxSummary && (
                <>
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle>{taxSummary.period.label}</CardTitle>
                      <CardDescription>
                        {taxSummary.period.start_date} to {taxSummary.period.end_date}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Income Summary */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg">Income Summary</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                              <span>Gross Revenue</span>
                              <span className="font-bold">{formatCurrency(taxSummary.income.gross_revenue)}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                              <span>Commission Income</span>
                              <span className="font-bold text-blue-600">{formatCurrency(taxSummary.income.commission_income)}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                              <span>Total Refunds</span>
                              <span className="font-bold text-red-600">-{formatCurrency(taxSummary.income.total_refunds)}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-green-50 rounded-lg border-2 border-green-200">
                              <span className="font-semibold">Net Commission</span>
                              <span className="font-bold text-green-600">{formatCurrency(taxSummary.income.net_commission)}</span>
                            </div>
                            <div className="flex justify-between p-3 bg-slate-100 rounded-lg">
                              <span>Total Bookings</span>
                              <span className="font-bold">{taxSummary.income.total_bookings}</span>
                            </div>
                          </div>
                        </div>

                        {/* Tax Estimates */}
                        <div className="space-y-4">
                          <h3 className="font-semibold text-lg">Tax Estimates</h3>
                          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                            <p className="text-sm text-amber-700 mb-4">{taxSummary.tax_estimates.note}</p>
                            <div className="space-y-3">
                              <div className="flex justify-between">
                                <span>Tax Rate</span>
                                <span className="font-bold">{formatPercent(taxSummary.tax_estimates.tax_rate)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Taxable Income</span>
                                <span className="font-bold">{formatCurrency(taxSummary.tax_estimates.taxable_income)}</span>
                              </div>
                              <div className="flex justify-between p-3 bg-white rounded-lg border-2 border-amber-300">
                                <span className="font-semibold">Estimated VAT</span>
                                <span className="font-bold text-amber-600">{formatCurrency(taxSummary.tax_estimates.estimated_vat)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Monthly Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Monthly Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left p-3">Month</th>
                              <th className="text-right p-3">Revenue</th>
                              <th className="text-right p-3">Commission</th>
                              <th className="text-right p-3">Bookings</th>
                            </tr>
                          </thead>
                          <tbody>
                            {taxSummary.monthly_breakdown?.map((m, i) => (
                              <tr key={`tax-month-${m.month || i}`} className="border-b hover:bg-slate-50">
                                <td className="p-3 font-medium">{m.month}</td>
                                <td className="text-right p-3">{formatCurrency(m.revenue)}</td>
                                <td className="text-right p-3 text-blue-600">{formatCurrency(m.commission)}</td>
                                <td className="text-right p-3">{m.bookings}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Export */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm font-medium text-gray-500">Quick Summary</h4>
                      <Button variant="outline" onClick={() => handleExport('tax-summary', 'csv')} data-testid="export-tax-summary-csv">
                        <Download className="w-4 h-4 mr-2" />
                        Export Summary (CSV)
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm font-medium text-gray-500">BIR VAT Report (Detailed)</h4>
                      <div className="flex gap-2 flex-wrap">
                        <Button variant="outline" onClick={() => handleExport('vat-report', 'csv')} data-testid="export-vat-report-csv">
                          <Download className="w-4 h-4 mr-2" />
                          VAT Report (CSV)
                        </Button>
                        <Button variant="default" onClick={() => handleExport('vat-report', 'pdf')} data-testid="export-vat-report-pdf">
                          <Download className="w-4 h-4 mr-2" />
                          VAT Report (PDF)
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Note: BIR VAT Report includes itemized transactions with customer, provider, and service details for compliance filing.
                  </p>
                </>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
